import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';

export interface BulkPreparationModalRef {
  open: (count: number) => void;
  close: () => void;
}

interface BulkPreparationModalProps {
  onConfirm?: (preparationType: 'picking_packing' | 'massive_packing') => void;
  onCancel?: () => void;
}

export const BulkPreparationModal = forwardRef<BulkPreparationModalRef, BulkPreparationModalProps>(
  ({ onConfirm, onCancel }, ref) => {
    const { t } = useTranslation('ordersShippedModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const [selectedType, setSelectedType] = useState<'picking_packing' | 'massive_packing'>('picking_packing');
    const [orderCount, setOrderCount] = useState(0);

    useImperativeHandle(ref, () => ({
      open: (count: number) => {
        setOrderCount(count);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleConfirm = () => {
      onConfirm?.(selectedType);
      modalRef.current?.closeModal();
    };

    const handleCancel = () => {
      onCancel?.();
      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter ref={modalRef} title={t('confirmModals.bulkPreparation.title')} type="default">
        <div className="space-y-4">
          <p className="text-sm">{t('confirmModals.bulkPreparation.message', { count: orderCount })}</p>

          <RadioGroup
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as 'picking_packing' | 'massive_packing')}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="picking_packing" id="picking_packing" />
              <label
                htmlFor="picking_packing"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                {t('confirmModals.bulkPreparation.options.picking_packing')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="massive_packing" id="massive_packing" />
              <label
                htmlFor="massive_packing"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                {t('confirmModals.bulkPreparation.options.massive_packing')}
              </label>
            </div>
          </RadioGroup>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" size="lg" onClick={handleCancel}>
              {t('buttons.cancel')}
              <EscIcon className="w-4 h-4" />
            </Button>
            <Button variant="primary" size="lg" onClick={handleConfirm}>
              {t('buttons.confirm')}
              <EnterIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

BulkPreparationModal.displayName = 'BulkPreparationModal';
